// Core packages
import { OnDestroy, OnInit } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material";
// Third party packages
import { CookieService } from "ngx-cookie-service";
import { of, throwError } from "rxjs";
import { switchMap } from "rxjs/operators";
// Custom packages
import { DocumentArchiveDownloadDialogComponent, } from "src/app/modules/properties/components/document-archive-download-dialog/document-archive-download-dialog.component";
import { DocumentArchiveDocsService } from "src/app/modules/properties/document-archive-docs.service";
import { exceedsRemakePlantLimit, mappingArchiveDocRows, MAPPING_DOCS_PAGE_SIZE, MAX_REMAKE_PLANT_DOCS, nextMappingDocsRead, selectedMappingDocIds, } from "src/app/modules/properties/mapping-archive-docs.utils";
import { ConfigService } from "src/app/shared/services/config.service";
/**
 * The documents of one mapping element on the 3.0.0 archive, before «Rifare impianto» and before
 * «Elimina impianto» / «Elimina la scheda» (MAP-A1).
 *
 * It stands in for the two legacy dialogs (`mapping-remake-dialog`, `mapping-delete-dialog`) once the
 * archive is switched on: same texts, same columns (Tipo documento, Descrizione, Data, «Scarica»),
 * same rows — the rule of `getDocsFromArchive`, defect included (@see mappingArchiveDocRows). What
 * changes is where the rows come from: `GET /document-archive-docs` for the element, every page of
 * it, read BEFORE the element is deleted, since the deletion withdraws them.
 *
 * Two cases the legacy dialogs never reached, because they crashed before opening:
 *  - the list cannot be read: «Rifare impianto» cannot be confirmed (without the list nothing can be
 *    chosen to carry over), the delete can (deleting loses no document);
 *  - more ticks than the back-end carries over: «Conferma» is off until some are removed, since with
 *    too many the back-end would copy nothing.
 *
 * «Scarica» opens the «Scarica i documenti» dialog of the new archive, which downloads through the
 * authenticated route of the row, never through a public path.
 *
 * Declared in MappingArchiveDocsDialogModule, imported by PropertiesModule: it is in the initial bundle
 * with the mapping tab that opens it.
 *
 * @since 3.0.0
 */
var MappingArchiveDocsDialogComponent = /** @class */ (function () {
    /**
     * Class constructor
     */
    function MappingArchiveDocsDialogComponent(dialogRef, data, dialog, configService, cookieService, documentArchiveDocsService) {
        this.dialogRef = dialogRef;
        this.data = data;
        this.dialog = dialog;
        this.configService = configService;
        this.cookieService = cookieService;
        this.documentArchiveDocsService = documentArchiveDocsService;
        this.subscriptions = [];
        /** Language of the session, for the names of the types */
        this.lang = "it";
        this.state = "loading";
        this.rows = [];
        /** The limit, for the message that names it */
        this.maxRemakeDocs = MAX_REMAKE_PLANT_DOCS;
    }
    /**
     * Read the documents of the element.
     *
     * Without both ids nothing is asked: a listing without `structureObjectId` would be the WHOLE
     * archive, offered as the documents of this element.
     *
     * @since 3.0.0
     */
    MappingArchiveDocsDialogComponent.prototype.ngOnInit = function () {
        var _this = this;
        this.lang = this.cookieService.get("lang") || this.configService.defaultLang;
        var archiveId = this.data && typeof this.data.documentArchiveId === "string" ? this.data.documentArchiveId : "";
        var elementId = this.data && typeof this.data.structureObjectId === "string" ? this.data.structureObjectId : "";
        if (!archiveId || !elementId) {
            this.state = "failed";
            return;
        }
        this.subscriptions.push(this.readElement(archiveId, elementId, 0, [], 1).subscribe(function (items) {
            _this.rows = mappingArchiveDocRows(items, _this.lang);
            _this.state = "loaded";
        }, function () {
            // The dialog says it in its body; a toast on top of it would only repeat it
            _this.rows = [];
            _this.state = "failed";
        }));
    };
    /**
     * Handle component destroy
     *
     * @since 3.0.0
     */
    MappingArchiveDocsDialogComponent.prototype.ngOnDestroy = function () {
        this.subscriptions.forEach(function (sub) { return sub.unsubscribe(); });
    };
    Object.defineProperty(MappingArchiveDocsDialogComponent.prototype, "isRemake", {
        /** True on «Rifare impianto» */
        get: function () {
            return !!this.data && this.data.mode === "remake";
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MappingArchiveDocsDialogComponent.prototype, "tooManySelected", {
        /** True when more rows are ticked than the back-end carries over */
        get: function () {
            return this.isRemake && exceedsRemakePlantLimit(selectedMappingDocIds(this.rows).length);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MappingArchiveDocsDialogComponent.prototype, "showNoDocs", {
        /**
         * The "no documents" line of the legacy dialogs: always on «Rifare impianto», on a plant only for
         * the delete. Not when the list failed: that says something else.
         */
        get: function () {
            return this.state === "loaded" && this.rows.length === 0 && (this.isRemake || !!this.data.sectionPlant);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MappingArchiveDocsDialogComponent.prototype, "canConfirm", {
        /** Whether «Conferma» is on (@see MappingArchiveDocsDialogComponent for the two failure cases) */
        get: function () {
            if (this.state === "loading") {
                return false;
            }
            if (this.isRemake) {
                return this.state === "loaded" && !this.tooManySelected;
            }
            return true;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MappingArchiveDocsDialogComponent.prototype, "messageKey", {
        /** The text of the legacy dialog this one stands in for */
        get: function () {
            return this.isRemake
                ? "properties.structures.textConfimFormRemake"
                : "properties.structures.textConfimFormDeletePlant";
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Open the files of a row in the «Scarica i documenti» dialog of the new archive.
     *
     * @since 3.0.0
     *
     * @param row The row whose «Scarica» was clicked
     */
    MappingArchiveDocsDialogComponent.prototype.onDownload = function (row) {
        if (!row || !row.docId || !Array.isArray(row.files) || !row.files.length) {
            return;
        }
        var dialogData = {
            docId: row.docId,
            files: row.files,
            barCode: row.barCode,
        };
        this.dialog.open(DocumentArchiveDownloadDialogComponent, {
            data: dialogData,
            width: "600px",
        });
    };
    /**
     * Handle dialog confirmation
     *
     * @since 3.0.0
     */
    MappingArchiveDocsDialogComponent.prototype.onConfirmation = function () {
        if (!this.canConfirm) {
            return;
        }
        var result = {
            documentArchiveDocIds: this.isRemake ? selectedMappingDocIds(this.rows) : [],
        };
        this.dialogRef.close(result);
    };
    /**
     * Handle dialog denial
     *
     * @since 3.0.0
     */
    MappingArchiveDocsDialogComponent.prototype.onDenial = function () {
        this.dialogRef.close();
    };
    /**
     * Every row of the element, page after page (@see nextMappingDocsRead for when it stops).
     *
     * An answer without its rows and a list too long to be read whole are errors, not short lists: a
     * short list would hide documents from the remake.
     */
    MappingArchiveDocsDialogComponent.prototype.readElement = function (archiveId, elementId, skip, readSoFar, page) {
        var _this = this;
        return this.documentArchiveDocsService
            .list({
            documentArchiveId: archiveId,
            structureObjectId: elementId,
            skip: skip,
            limit: MAPPING_DOCS_PAGE_SIZE,
        })
            .pipe(switchMap(function (res) {
            if (!res || !res.status || !res.data || !Array.isArray(res.data.items)) {
                return throwError(new Error("The listing of the element came back without its rows"));
            }
            var items = readSoFar.concat(res.data.items);
            var total = typeof res.data.totalCount === "number" ? res.data.totalCount : items.length;
            var next = nextMappingDocsRead(items.length, res.data.items.length, total, page);
            if (next.kind === "next") {
                return _this.readElement(archiveId, elementId, next.skip, items, page + 1);
            }
            if (next.kind === "tooMany") {
                return throwError(new Error("The listing of the element is too long to be read whole"));
            }
            return of(items);
        }));
    };
    return MappingArchiveDocsDialogComponent;
}());
export { MappingArchiveDocsDialogComponent };
